import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { SecureStorage } from './storage/SecureStorage';
import { ApiError as AppApiError } from '@/types/residents';
import { AppConfig } from '@/constants/residents/config';
import { performTokenRefresh } from '@/services/shared/auth/refreshTokenHelper';
import { store } from '@/store';
import { logoutAndClear } from '@/services/shared/auth/logoutAndClear';
import { isNetworkError } from '@/utils/common/isNetworkError';
import { getServerErrorMessage } from '@/utils/common/serverError';
import { notifyGatewayError } from '@/constants/common/gatewayErrorMessages';
import { addRedirectBreadcrumb } from '@/services/observability/redirectTelemetry';
import { MAX_RETRIES, delay, isRetriableStatus, nextRetryDelayMs } from '@/store/api/retryPolicy';

/**
 * Auth endpoints have no established session to refresh, so a 401/403 from them
 * is a genuine auth failure — never a stale-token signal. Both interceptors use
 * this to skip bearer injection (request) and, crucially, token refresh
 * (response): refreshing on a failed login/signup masked the server's real
 * error behind a generic refresh/logout result. `/refresh` itself is included
 * so a 401 from the refresh call cannot re-enter the refresh path and
 * circularly await the in-flight refresh promise (a ~10s hang until timeout).
 *
 * Fragments are matched against the resolved endpoint paths, e.g. signup is
 * `/api/v2/users/account` (not `/signup`).
 */
const AUTH_ENDPOINT_FRAGMENTS = [
  '/login',
  '/users/account',
  '/forgot-password',
  '/users/set-password',
  '/users/verification',
  '/refresh',
];

const isAuthEndpoint = (url?: string): boolean =>
  !!url && AUTH_ENDPOINT_FRAGMENTS.some((fragment) => url.includes(fragment));

// Rejected verbatim on a failed-refresh logout. Built directly rather than via
// `formatError` so the friendly copy isn't overwritten by the original 401
// body's `error` field (e.g. "jwt expired").
const SESSION_EXPIRED_ERROR: AppApiError = {
  code: 'SESSION_EXPIRED',
  message: 'Session expired. Please log in again.',
};

export class BaseAPI {
  protected api: AxiosInstance;
  private static instance: BaseAPI;
  private isRefreshingToken = false;
  private refreshTokenPromise: Promise<boolean> | null = null;

  protected constructor() {
    this.api = axios.create({
      baseURL: AppConfig.API.BASE_URL,
      timeout: AppConfig.API.TIMEOUT,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  static getInstance(): BaseAPI {
    if (!BaseAPI.instance) {
      BaseAPI.instance = new BaseAPI();
    }
    return BaseAPI.instance;
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.api.interceptors.request.use(
      async (config) => {
        // Don't add auth headers for authentication endpoints
        const authEndpoint = isAuthEndpoint(config.url);

        // Don't override existing Authorization header (for admin Basic Auth)
        const hasAuthHeader = config.headers?.Authorization;

        if (!authEndpoint && !hasAuthHeader) {
          const token = await SecureStorage.getInstance().getToken();
          if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => {
        // Redirect telemetry (no-op until Sentry lands) — RES-102.
        try {
          const requestedUrl = axios.getUri({
            baseURL: response.config.baseURL,
            url: response.config.url,
          });
          addRedirectBreadcrumb('axios', requestedUrl, (response.request as any)?.responseURL, undefined);
        } catch {
          // Intentionally silent: telemetry must never throw or alter response flow.
        }
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as any;
        
        // Handle both 401 (unauthorized) and 403 with "jwt expired" for consistency
        // with RTK Query — but never for auth endpoints, whose failures are real
        // and must surface the server's message instead of triggering a refresh.
        // Dual-shape read (legacy string body and unified envelope) — RES-102.
        const shouldRefresh = !isAuthEndpoint(originalRequest?.url) &&
                             (error.response?.status === 401 ||
                             (error.response?.status === 403 &&
                              getServerErrorMessage(error) === 'jwt expired'));
        
        // Prevent infinite refresh loops
        if (shouldRefresh && !originalRequest._isRetry) {
          
          try {
            // Use instance-level promise to prevent multiple simultaneous refreshes
            if (!this.refreshTokenPromise) {
              this.isRefreshingToken = true;
              this.refreshTokenPromise = performTokenRefresh(this.api);
            }
            
            const refreshed = await this.refreshTokenPromise;
            
            if (refreshed && originalRequest) {
              
              // Mark request as retry to prevent infinite loops
              originalRequest._isRetry = true;
              
              // Update Authorization header with new token
              const newToken = await SecureStorage.getInstance().getToken();
              if (newToken && originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
              }
              
              // Reset refresh state
              this.isRefreshingToken = false;
              this.refreshTokenPromise = null;
              
              // Retry original request
              return this.api.request(originalRequest);
            } else {
              console.error('🔴 BaseAPI: Token refresh failed, logging out');
              this.isRefreshingToken = false;
              this.refreshTokenPromise = null;
              
              // Refresh failed, logout user
              await this.handleLogout();
              
              // Return a specific error for failed refresh
              return Promise.reject(SESSION_EXPIRED_ERROR);
            }
          } catch (refreshError) {
            console.error('🔴 BaseAPI: Token refresh error:', refreshError);
            this.isRefreshingToken = false;
            this.refreshTokenPromise = null;
            
            // Refresh failed, logout user
            await this.handleLogout();
            
            return Promise.reject(SESSION_EXPIRED_ERROR);
          }
        }
        
        // Bounded auto-retry for transient statuses — idempotent only (RES-114).
        const status = error.response?.status;
        const cfgMethod = (originalRequest?.method ?? 'get').toUpperCase();
        const retriable = (cfgMethod === 'GET' || cfgMethod === 'HEAD') && isRetriableStatus(status);
        if (retriable) {
          const attempt = originalRequest._retryAttempt ?? 0;
          const retryAfterHeader = (error.response?.headers as any)?.['retry-after'];
          const waitMs = nextRetryDelayMs(status as number, attempt, retryAfterHeader);
          // Skip the retry when: retry-after exceeds the 60s ceiling (waitMs ===
          // null → fail now, not a multi-minute hang), the cap is reached, or the
          // consumer cancelled — don't schedule the sleep or re-fetch. The abort
          // can also arrive mid-sleep, hence the second check below.
          if (waitMs !== null && attempt < MAX_RETRIES && !originalRequest?.signal?.aborted) {
            originalRequest._retryAttempt = attempt + 1;
            await delay(waitMs);
            if (!originalRequest?.signal?.aborted) {
              return this.api.request(originalRequest);
            }
          }
        }

        const gatewayStatus = error.response?.status;
        if (gatewayStatus === 502 || gatewayStatus === 503 || gatewayStatus === 504) {
          console.error(`🔴 BaseAPI gateway error: ${gatewayStatus} on ${originalRequest?.url}`);
          notifyGatewayError(gatewayStatus);
        }

        return Promise.reject(this.formatError(error));
      }
    );
  }

  private async handleLogout(): Promise<void> {
    try {
      await logoutAndClear(store.dispatch);
    } catch (error) {
      console.error('🔴 BaseAPI: Error during logout:', error);
    }
  }

  private formatError(error: AxiosError): AppApiError {
    if (error.response) {
      // Server responded with error
      const data = error.response.data as any;
      // The backend error envelope carries the human-readable message in
      // `error` (e.g. `{ error: "Invalid credentials." }`); prefer it over the
      // generic axios `error.message` ("Request failed with status code 401"),
      // but only when it's actually a string — some endpoints return a
      // structured `error` object, which must not become `ApiError.message`.
      const serverMessage =
        typeof data?.message === 'string'
          ? data.message
          : typeof data?.error === 'string'
            ? data.error
            : undefined;
      return {
        code: data?.code || error.response.status.toString(),
        message: serverMessage || error.message,
        details: data?.details,
      };
    } else if (isNetworkError(error) || error.request) {
      // Request made but no response
      return {
        code: 'NETWORK_ERROR',
        message: 'Network error. Please check your connection.',
      };
    } else {
      // Something else happened
      return {
        code: 'UNKNOWN_ERROR',
        message: error.message || 'An unknown error occurred',
      };
    }
  }

  // Helper methods for API calls
  protected async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.get<T>(url, config);
    return response.data;
  }

  protected async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.post<T>(url, data, config);
    return response.data;
  }

  protected async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.put<T>(url, data, config);
    return response.data;
  }

  protected async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.delete<T>(url, config);
    return response.data;
  }

  protected async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.patch<T>(url, data, config);
    return response.data;
  }
} 